import { Connection, WsClient } from "../WsClient";
import _ from "lodash";
import {
  ClientStatus,
  EndpointStatus,
  EndpointStatusDTO,
  ExpertDTO,
  IncidentType,
  KioskDTO,
  RemSignal,
  RoomDTO,
  SessionInfo,
  TelephonyType
} from "../types";
import { AbstractService } from "./AbstractService";
import { ConfigurableInterface } from "./ConfigurableInterface";
import { detect } from "detect-browser";
import { v4 as uuidv4 } from "uuid";
import { getDeviceType, getOsVersion } from "../utils";

export interface RemServiceInterface extends ConfigurableInterface {
  getTelephonyType(callback: Function, errorCallback?: Function): void;
  getTopics(callback: Function, errorCallback?: Function): void;
  getKioskTopics(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ): void;
  getAvailableTopics(callback: Function, errorCallback?: Function): void;
  getSessionInfo(callback: Function, errorCallback?: Function): void;
  getStatus(callback: Function, errorCallback?: Function): void;
  setStatus(
    status: ClientStatus,
    callback: Function,
    errorCallback?: Function
  ): void;
  getEndpointStatus(callback: Function, errorCallback?: Function): void;
  setEndpointStatus(
    status: EndpointStatus | number,
    details: string,
    parties: string[],
    callback: Function,
    errorCallback?: Function
  ): void;
  getHints(callback: Function, errorCallback?: Function): void;
  acquire(
    target: string,
    foreignId: string,
    context: object,
    callback: Function,
    errorCallback?: Function
  ): void;
  removeFromQueue(callback: Function, errorCallback?: Function): void;
  getQueuePosition(callback: Function, errorCallback?: Function): void;
  getQueueList(callback: Function, errorCallback?: Function): void;
  sendCallJoinRequest(
    extension: string,
    personId: string,
    webexRoomId: string,
    callback: Function,
    errorCallback?: Function
  ): void;
  sendJsonMessage(
    message: {},
    correlationId: string | null,
    callback: Function,
    errorCallback?: Function
  ): void;
  subscribeOnSignals(
    callbacks: Record<string, Function>,
    callback?: Function
  ): void;
  setSignalCallback(signal: RemSignal, callback: Function): void;
  setNetworkCallbacks(
    callbacks: Record<"onLost" | "onReconnect", Function>
  ): void;
  unsubscribeFromSignals(): void;
  sendSignalResponse(
    requestId: number,
    isSuccess: boolean,
    message: string,
    callback: Function,
    errorCallback?: Function
  ): void;
  getAvailableExperts(callback: Function, errorCallback?: Function): void;
  getExperts(callback: Function, errorCallback?: Function): void;
  getKiosks(
    callback: (kiosks: KioskDTO[]) => void,
    errorCallback?: Function
  ): void;
  requestCall(
    deviceId: string,
    callback: Function,
    errorCallback?: Function
  ): void;
  transferCall(
    expert: ExpertDTO,
    callback: Function,
    errorCallback?: Function
  ): void;
  changeCallTopic(
    topic: string,
    callback: Function,
    errorCallback?: Function
  ): void;

  answerCall(callback: Function, errorCallback?: Function): void;

  hangup(callback: Function, errorCallback?: Function): void;

  getRoom(callback: Function, errorCallback?: Function): void;

  getRooms(callback: Function, errorCallback?: Function): void;

  setActiveRoom(id: string, callback: Function, errorCallback?: Function): void;

  removeRoom(callback: Function, errorCallback?: Function): void;

  startSurvey(callback: Function, errorCallback?: Function): void;

  holdCall(callback: Function, errorCallback?: Function): void;

  resumeCall(callback: Function, errorCallback?: Function): void;

  getRtcConnectionConfig(callback: Function, errorCallback?: Function): void;

  sendToHub(
    params: {
      receiverId: string;
      serviceName: string;
      message: {};
      messageId: string;
      isError?: boolean;
    },
    callback: Function,
    errorCallback?: Function
  ): void;

  setStatistic(
    data: object,
    callback: Function,
    errorCallback?: Function
  ): void;

  setClientContext(
    data: object,
    callback: Function,
    errorCallback?: Function
  ): void;

  getIncident(callback: Function, errorCallback?: Function): void;

  startProcessingIncident(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ): void;

  closeIncident(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ): void;

  createTicket(
    incidentUuid: string,
    payload: object,
    callback: Function,
    errorCallback?: Function
  ): void;

  getTicketsCount(callback: Function, errorCallback?: Function): void;

  getTicket(callback: Function, errorCallback?: Function): void;

  closeTicket(
    uuid: string,
    payload: object,
    callback: Function,
    errorCallback?: Function
  ): void;

  disconnect(): void;
}

export class RemService extends AbstractService implements RemServiceInterface {
  private isConnected = false;
  private signalCallbacks: Record<string, Function> = {};
  private networkCallbacks: Record<
    "onLost" | "onReconnect",
    Function
  > | null = null;
  private getConnectionUrl(): string {
    this.checkConfig(["wbdServiceUrl", "remServicePort"]);
    return `${this.config.wbdServiceUrl}?port=${this.config.remServicePort}`;
  }

  private onReconnect(callback?: Function) {
    this.isConnected = false;

    if (_.keys(this.signalCallbacks).length) {
      this.subscribeOnSignals(this.signalCallbacks, () => {
        this.networkCallbacks?.onReconnect?.();
        callback?.();
      });
    } else {
      this.networkCallbacks?.onReconnect?.();
      callback?.();
    }
  }

  private getWsConnection(): Connection {
    const wsClient = WsClient.getInstance();
    const url = this.getConnectionUrl();

    return (
      wsClient.getConnection(url) ??
      wsClient.openConnection(
        url,
        {
          onReconnect: () => this.onReconnect(),
          onLost: this.networkCallbacks?.onLost
        },
        {
          time: 45,
          ping: {
            request: {
              method: `/${this.config.clientType}/ping`,
              host: this.config.remServiceUrl ?? "",
              jwt: this.config.jwt,
              body: [],
              callback: () => console.log("A ping call to REM has been sent"),
              errorCallback: () =>
                console.error("A ping call to REM has been sent")
            },
            interval: 5
          }
        }
      )
    );
  }

  private connect(callback: Function) {
    this.checkConfig([
      "remServiceUrl",
      "clientName",
      "clientId",
      "extension",
      "clientType",
      "jwt"
    ]);

    /* eslint-disable */
    // @ts-ignore
    const ip = require('ip');
    /* eslint-enable */

    const connection = this.getWsConnection();

    this.isConnected = true;

    const browser = detect();

    connection.sendRequest({
      isPrioritized: true,
      method: `/${this.config.clientType}/connect`,
      host: this.config.remServiceUrl ?? "",
      jwt: this.config.jwt,
      body: [
        _.assign(
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_info: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              browser_name: browser?.name,
              // eslint-disable-next-line @typescript-eslint/camelcase
              browser_version: browser?.version,
              os: browser?.os,
              // eslint-disable-next-line @typescript-eslint/camelcase
              os_version: getOsVersion() ?? "",
              // eslint-disable-next-line @typescript-eslint/camelcase
              screen_size: {
                width: window.screen.width,
                height: window.screen.height
              },
              // eslint-disable-next-line @typescript-eslint/camelcase
              device_type: getDeviceType()
            },
            id: this.config.clientId,
            name: this.config.clientName,
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_name: this.config.clientDisplayName || "",
            extension: this.config.extension,
            language: 0,
            languages: [],
            status:
              this.config.clientType === "kiosk"
                ? ClientStatus.ready
                : ClientStatus.unavailable,
            details: "",
            // eslint-disable-next-line @typescript-eslint/camelcase
            ip_addresses: [ip.address()],
            capabilities: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              interactive_desktop_sharing:
                this.config.clientType === "kiosk" &&
                this.config.extension === this.config.clientId
            }
          },
          this.config.clientType === "kiosk"
            ? {
                // eslint-disable-next-line @typescript-eslint/camelcase
                device_id: this.config.extension
              }
            : this.config.mustangExtension
            ? {
                // eslint-disable-next-line @typescript-eslint/camelcase
                mustang_extension: this.config.mustangExtension // property: cisco.sip.extension
              }
            : {},
          _.keys(this.config).includes("timezone")
            ? // eslint-disable-next-line @typescript-eslint/camelcase
              { time_zone: this.config.timezone }
            : {}
        )
      ],
      callback: (data: unknown) => {
        this.isConnected = true;
        callback?.(data);
      },
      errorCallback: () => {
        this.isConnected = false;
        console.error("Can not connect to REM");
      }
    });
  }

  private connectIfNotWithCallback(callback: Function) {
    if (this.isConnected) {
      callback();
    } else {
      this.connect(() => callback());
    }
  }

  public disconnect() {
    const wsClient = WsClient.getInstance();
    wsClient.closeConnection(this.getConnectionUrl());

    this.isConnected = false;
    this.signalCallbacks = {};
  }

  public getTelephonyType(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.getWsConnection().sendRequest(
      _.assign(
        {
          method: "/expert/get_telephony_type",
          host: this.config.remServiceUrl!,
          jwt: this.config.jwt,
          callback
        },
        errorCallback ? { errorCallback } : {}
      )
    );
  }

  public getTopics(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/get_topics`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
  public getKioskTopics(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/get_topics_for`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [uuid],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getAvailableTopics(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/get_available_topics",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getSessionInfo(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/get_session_info",
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            callback: (data: SessionInfo) => callback(data)
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getStatus(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/get_status",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getEndpointStatus(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/get_endpoint_status`,
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            callback: (data: EndpointStatusDTO) => callback(data)
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public setStatus(
    status: ClientStatus,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/set_status",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [status, ""],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public setEndpointStatus(
    status: EndpointStatus | number,
    details: string,
    parties: string[],
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "extension", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/set_endpoint_status`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [
              {
                extension: this.config.extension,
                status,
                details,
                parties
              }
            ],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getHints(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.getWsConnection().sendRequest(
      _.assign(
        {
          method: "/queue/hints",
          host: this.config.remServiceUrl!,
          jwt: this.config.jwt,
          body: [],
          callback
        },
        errorCallback ? { errorCallback } : {}
      )
    );
  }

  public acquire(
    target: string,
    foreignId: string,
    context: object,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/acquire",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [target, foreignId, context],
            callback: (position: number) => callback(position)
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public removeFromQueue(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/release",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getQueuePosition(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/position",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
  public getQueueList(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/list",
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            body: [],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public sendCallJoinRequest(
    extension: string,
    personId: string,
    webexRoomId: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/kiosk/membership_request",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [
              {
                // eslint-disable-next-line @typescript-eslint/camelcase
                creator_extension: extension,
                // eslint-disable-next-line @typescript-eslint/camelcase
                person_id: personId,
                // eslint-disable-next-line @typescript-eslint/camelcase
                room_id: webexRoomId
              }
            ],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public sendJsonMessage(
    message: {},
    correlationId: string | null,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/send_message`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [
              "application/json",
              btoa(unescape(encodeURIComponent(JSON.stringify(message)))),
              correlationId ?? "{00000000-0000-0000-0000-000000000000}"
            ],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public subscribeOnSignals(
    callbacks: Record<string, Function>,
    callback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "jwt", "clientType", "telephonyType"]);
    this.signalCallbacks = callbacks;

    console.log("Subscribing on signals...");

    const clientType = this.config.clientType,
      telephonyType = this.config.telephonyType;

    const targetSignals: Record<RemSignal, string> = _.fromPairs(
      [
        [RemSignal.message, "uuid,mime_type,binary,uuid,uuid"],
        [
          RemSignal.endpointStatusChanged,
          "uuid,struct<string,string,set<string>,uint>"
        ],
        [RemSignal.shutdownRequested, "int"]
      ]
        .concat(
          clientType === "kiosk"
            ? [
                [RemSignal.positionChanged, "uuid,int,int"],
                [
                  RemSignal.incidentChanged,
                  "struct<ulong,uuid,bool,string,enum<IncidentTypeNone=-1,IncidentTypeQueueTimeout=0,IncidentTypeNoExperts=1>>"
                ]
              ]
            : []
        )
        .concat(
          clientType === "kiosk" && telephonyType === TelephonyType.webex
            ? [
                [RemSignal.callRequested, "uuid,string,long"],
                [RemSignal.membershipRequested, "uuid,string,string"]
              ]
            : []
        )
        .concat(
          telephonyType === TelephonyType.webex
            ? [[RemSignal.hangupRequested, "uuid,long"]]
            : []
        )
        .concat(
          clientType === "expert"
            ? [
                [RemSignal.recvFrom, "uuid,string,string,uuid,bool"],
                [
                  RemSignal.statusChanged,
                  "uuid,enum<ClientFailure=-1,ClientNotConnected=0,ClientUnavailable=1,ClientReady=2,ClientBusy=3>,enum<StatusChangedUnknown=0,StatusChangedClient=1,StatusChangedAcquisitionTimeout=2,StatusChangedEndpointTimeout=3>"
                ],
                [
                  RemSignal.sessionInfoChanged,
                  "uuid,struct<struct<bool,bool>,json,uuid,struct<string,string,set<string>,uint>,uint,struct<string,string,uint,string,string,struct<int,int>>,string,struct<string,string,set<string>,uint>,string,string,uuid,set<string>,string,bool,string,string,uint,enum<Utc=0,Africa_Abidjan=1,Africa_Accra=2,Africa_Addis_Ababa=3,Africa_Algiers=4,Africa_Asmara=5,Africa_Bamako=6,Africa_Bangui=7,Africa_Banjul=8,Africa_Bissau=9,Africa_Blantyre=10,Africa_Brazzaville=11,Africa_Bujumbura=12,Africa_Cairo=13,Africa_Casablanca=14,Africa_Ceuta=15,Africa_Conakry=16,Africa_Dakar=17,Africa_Dar_es_Salaam=18,Africa_Djibouti=19,Africa_Douala=20,Africa_El_Aaiun=21,Africa_Freetown=22,Africa_Gaborone=23,Africa_Harare=24,Africa_Johannesburg=25,Africa_Juba=26,Africa_Kampala=27,Africa_Khartoum=28,Africa_Kigali=29,Africa_Kinshasa=30,Africa_Lagos=31,Africa_Libreville=32,Africa_Lome=33,Africa_Luanda=34,Africa_Lubumbashi=35,Africa_Lusaka=36,Africa_Malabo=37,Africa_Maputo=38,Africa_Maseru=39,Africa_Mbabane=40,Africa_Mogadishu=41,Africa_Monrovia=42,Africa_Nairobi=43,Africa_Ndjamena=44,Africa_Niamey=45,Africa_Nouakchott=46,Africa_Ouagadougou=47,Africa_Porto_Novo=48,Africa_Sao_Tome=49,Africa_Tripoli=50,Africa_Tunis=51,Africa_Windhoek=52,America_Adak=53,America_Anchorage=54,America_Anguilla=55,America_Antigua=56,America_Araguaina=57,America_Argentina_Buenos_Aires=58,America_Argentina_Catamarca=59,America_Argentina_Cordoba=60,America_Argentina_Jujuy=61,America_Argentina_La_Rioja=62,America_Argentina_Mendoza=63,America_Argentina_Rio_Gallegos=64,America_Argentina_Salta=65,America_Argentina_San_Juan=66,America_Argentina_San_Luis=67,America_Argentina_Tucuman=68,America_Argentina_Ushuaia=69,America_Aruba=70,America_Asuncion=71,America_Atikokan=72,America_Bahia=73,America_Bahia_Banderas=74,America_Barbados=75,America_Belem=76,America_Belize=77,America_Blanc_Sablon=78,America_Boa_Vista=79,America_Bogota=80,America_Boise=81,America_Cambridge_Bay=82,America_Campo_Grande=83,America_Cancun=84,America_Caracas=85,America_Cayenne=86,America_Cayman=87,America_Chicago=88,America_Chihuahua=89,America_Costa_Rica=90,America_Creston=91,America_Cuiaba=92,America_Curacao=93,America_Danmarkshavn=94,America_Dawson=95,America_Dawson_Creek=96,America_Denver=97,America_Detroit=98,America_Dominica=99,America_Edmonton=100,America_Eirunepe=101,America_El_Salvador=102,America_Fortaleza=103,America_Glace_Bay=104,America_Godthab=105,America_Goose_Bay=106,America_Grand_Turk=107,America_Grenada=108,America_Guadeloupe=109,America_Guatemala=110,America_Guayaquil=111,America_Guyana=112,America_Halifax=113,America_Havana=114,America_Hermosillo=115,America_Indiana_Indianapolis=116,America_Indiana_Knox=117,America_Indiana_Marengo=118,America_Indiana_Petersburg=119,America_Indiana_Tell_City=120,America_Indiana_Vevay=121,America_Indiana_Vincennes=122,America_Indiana_Winamac=123,America_Inuvik=124,America_Iqaluit=125,America_Jamaica=126,America_Juneau=127,America_Kentucky_Louisville=128,America_Kentucky_Monticello=129,America_Kralendijk=130,America_La_Paz=131,America_Lima=132,America_Los_Angeles=133,America_Lower_Princes=134,America_Maceio=135,America_Managua=136,America_Manaus=137,America_Marigot=138,America_Martinique=139,America_Matamoros=140,America_Mazatlan=141,America_Menominee=142,America_Merida=143,America_Metlakatla=144,America_Mexico_City=145,America_Miquelon=146,America_Moncton=147,America_Monterrey=148,America_Montevideo=149,America_Montserrat=150,America_Nassau=151,America_New_York=152,America_Nipigon=153,America_Nome=154,America_Noronha=155,America_North_Dakota_Beulah=156,America_North_Dakota_Center=157,America_North_Dakota_New_Salem=158,America_Ojinaga=159,America_Panama=160,America_Pangnirtung=161,America_Paramaribo=162,America_Phoenix=163,America_Port_au_Prince=164,America_Port_of_Spain=165,America_Porto_Velho=166,America_Puerto_Rico=167,America_Rainy_River=168,America_Rankin_Inlet=169,America_Recife=170,America_Regina=171,America_Resolute=172,America_Rio_Branco=173,America_Santa_Isabel=174,America_Santarem=175,America_Santiago=176,America_Santo_Domingo=177,America_Sao_Paulo=178,America_Scoresbysund=179,America_Sitka=180,America_St_Barthelemy=181,America_St_Johns=182,America_St_Kitts=183,America_St_Lucia=184,America_St_Thomas=185,America_St_Vincent=186,America_Swift_Current=187,America_Tegucigalpa=188,America_Thule=189,America_Thunder_Bay=190,America_Tijuana=191,America_Toronto=192,America_Tortola=193,America_Vancouver=194,America_Whitehorse=195,America_Winnipeg=196,America_Yakutat=197,America_Yellowknife=198,Antarctica_Casey=199,Antarctica_Davis=200,Antarctica_DumontDUrville=201,Antarctica_Macquarie=202,Antarctica_Mawson=203,Antarctica_McMurdo=204,Antarctica_Palmer=205,Antarctica_Rothera=206,Antarctica_Syowa=207,Antarctica_Troll=208,Antarctica_Vostok=209,Arctic_Longyearbyen=210,Asia_Aden=211,Asia_Almaty=212,Asia_Amman=213,Asia_Anadyr=214,Asia_Aqtau=215,Asia_Aqtobe=216,Asia_Ashgabat=217,Asia_Baghdad=218,Asia_Bahrain=219,Asia_Baku=220,Asia_Bangkok=221,Asia_Beirut=222,Asia_Bishkek=223,Asia_Brunei=224,Asia_Chita=225,Asia_Choibalsan=226,Asia_Colombo=227,Asia_Damascus=228,Asia_Dhaka=229,Asia_Dili=230,Asia_Dubai=231,Asia_Dushanbe=232,Asia_Gaza=233,Asia_Hebron=234,Asia_Ho_Chi_Minh=235,Asia_Hong_Kong=236,Asia_Hovd=237,Asia_Irkutsk=238,Asia_Jakarta=239,Asia_Jayapura=240,Asia_Jerusalem=241,Asia_Kabul=242,Asia_Kamchatka=243,Asia_Karachi=244,Asia_Kathmandu=245,Asia_Khandyga=246,Asia_Kolkata=247,Asia_Krasnoyarsk=248,Asia_Kuala_Lumpur=249,Asia_Kuching=250,Asia_Kuwait=251,Asia_Macau=252,Asia_Magadan=253,Asia_Makassar=254,Asia_Manila=255,Asia_Muscat=256,Asia_Nicosia=257,Asia_Novokuznetsk=258,Asia_Novosibirsk=259,Asia_Omsk=260,Asia_Oral=261,Asia_Phnom_Penh=262,Asia_Pontianak=263,Asia_Pyongyang=264,Asia_Qatar=265,Asia_Qyzylorda=266,Asia_Rangoon=267,Asia_Riyadh=268,Asia_Sakhalin=269,Asia_Samarkand=270,Asia_Seoul=271,Asia_Shanghai=272,Asia_Singapore=273,Asia_Srednekolymsk=274,Asia_Taipei=275,Asia_Tashkent=276,Asia_Tbilisi=277,Asia_Tehran=278,Asia_Thimphu=279,Asia_Tokyo=280,Asia_Ulaanbaatar=281,Asia_Urumqi=282,Asia_Ust_Nera=283,Asia_Vientiane=284,Asia_Vladivostok=285,Asia_Yakutsk=286,Asia_Yekaterinburg=287,Asia_Yerevan=288,Atlantic_Azores=289,Atlantic_Bermuda=290,Atlantic_Canary=291,Atlantic_Cape_Verde=292,Atlantic_Faroe=293,Atlantic_Madeira=294,Atlantic_Reykjavik=295,Atlantic_South_Georgia=296,Atlantic_St_Helena=297,Atlantic_Stanley=298,Australia_Adelaide=299,Australia_Brisbane=300,Australia_Broken_Hill=301,Australia_Currie=302,Australia_Darwin=303,Australia_Eucla=304,Australia_Hobart=305,Australia_Lindeman=306,Australia_Lord_Howe=307,Australia_Melbourne=308,Australia_Perth=309,Australia_Sydney=310,Europe_Amsterdam=311,Europe_Andorra=312,Europe_Athens=313,Europe_Belgrade=314,Europe_Berlin=315,Europe_Bratislava=316,Europe_Brussels=317,Europe_Bucharest=318,Europe_Budapest=319,Europe_Busingen=320,Europe_Chisinau=321,Europe_Copenhagen=322,Europe_Dublin=323,Europe_Gibraltar=324,Europe_Guernsey=325,Europe_Helsinki=326,Europe_Isle_of_Man=327,Europe_Istanbul=328,Europe_Jersey=329,Europe_Kaliningrad=330,Europe_Kiev=331,Europe_Lisbon=332,Europe_Ljubljana=333,Europe_London=334,Europe_Luxembourg=335,Europe_Madrid=336,Europe_Malta=337,Europe_Mariehamn=338,Europe_Minsk=339,Europe_Monaco=340,Europe_Moscow=341,Europe_Oslo=342,Europe_Paris=343,Europe_Podgorica=344,Europe_Prague=345,Europe_Riga=346,Europe_Rome=347,Europe_Samara=348,Europe_San_Marino=349,Europe_Sarajevo=350,Europe_Simferopol=351,Europe_Skopje=352,Europe_Sofia=353,Europe_Stockholm=354,Europe_Tallinn=355,Europe_Tirane=356,Europe_Uzhgorod=357,Europe_Vaduz=358,Europe_Vatican=359,Europe_Vienna=360,Europe_Vilnius=361,Europe_Volgograd=362,Europe_Warsaw=363,Europe_Zagreb=364,Europe_Zaporozhye=365,Europe_Zurich=366,Indian_Antananarivo=367,Indian_Chagos=368,Indian_Christmas=369,Indian_Cocos=370,Indian_Comoro=371,Indian_Kerguelen=372,Indian_Mahe=373,Indian_Maldives=374,Indian_Mauritius=375,Indian_Mayotte=376,Indian_Reunion=377,Pacific_Apia=378,Pacific_Auckland=379,Pacific_Bougainville=380,Pacific_Chatham=381,Pacific_Chuuk=382,Pacific_Easter=383,Pacific_Efate=384,Pacific_Enderbury=385,Pacific_Fakaofo=386,Pacific_Fiji=387,Pacific_Funafuti=388,Pacific_Galapagos=389,Pacific_Gambier=390,Pacific_Guadalcanal=391,Pacific_Guam=392,Pacific_Honolulu=393,Pacific_Johnston=394,Pacific_Kiritimati=395,Pacific_Kosrae=396,Pacific_Kwajalein=397,Pacific_Majuro=398,Pacific_Marquesas=399,Pacific_Midway=400,Pacific_Nauru=401,Pacific_Niue=402,Pacific_Norfolk=403,Pacific_Noumea=404,Pacific_Pago_Pago=405,Pacific_Palau=406,Pacific_Pitcairn=407,Pacific_Pohnpei=408,Pacific_Port_Moresby=409,Pacific_Rarotonga=410,Pacific_Saipan=411,Pacific_Tahiti=412,Pacific_Tarawa=413,Pacific_Tongatapu=414,Pacific_Wake=415,Pacific_Wallis=416>,string,map<enum<AnyLanguage=0,Arabic=8,Chinese=25,English=31,French=37,German=42,Hebrew=48,Italian=58,Portuguese=91,Russian=96,Spanish=111>,string>,map<enum<AnyLanguage=0,Arabic=8,Chinese=25,English=31,French=37,German=42,Hebrew=48,Italian=58,Portuguese=91,Russian=96,Spanish=111>,string>>"
                ],
                [
                  RemSignal.availableTopicsChanged,
                  "map<string,set<enum<AnyLanguage=0,Arabic=8,Chinese=25,English=31,French=37,German=42,Hebrew=48,Italian=58,Portuguese=91,Russian=96,Spanish=111>>>"
                ],
                [RemSignal.ticketsCountChanged, "struct<uint,uint>"]
              ]
            : []
        )
        .concat(
          clientType === "expert" && telephonyType === TelephonyType.webex
            ? [
                [RemSignal.holdRequested, "uuid,bool,long"],
                [RemSignal.answerRequested, "uuid,long"]
              ]
            : []
        )
        .concat(
          telephonyType === TelephonyType.twilio
            ? [
                [RemSignal.twilioJoinRequested, "string,string,long"],
                [RemSignal.twilioLeaveRequested, "string,long"]
              ]
            : []
        )
    ) as Record<RemSignal, string>;

    let currentSignal: RemSignal | null = null;

    const subscribe = () => {
      const getNextSignal = (): RemSignal | null => {
        if (currentSignal === null) {
          return (_.keys(targetSignals) as RemSignal[])[0] ?? null;
        }
        const index = _.findIndex(
          _.keys(targetSignals),
          s => s === currentSignal
        );

        return (_.keys(targetSignals) as RemSignal[])[index + 1] ?? null;
      };

      const signal = getNextSignal();

      if (!signal) {
        callback?.();
        return;
      }

      console.log(`${signal} signal subscribing...`);

      this.connectIfNotWithCallback(() =>
        this.getWsConnection().sendRequest({
          isPrioritized: true,
          method: "/rsignal/connect",
          host: this.config.remServiceUrl ?? "",
          jwt: this.config.jwt,
          body: [
            this.getSignalNamespace(signal),
            signal,
            targetSignals[signal]
          ],
          callback: (data: unknown) => {
            console.log(`${signal} signal subscribed`);

            if (signal in callbacks) {
              this.getWsConnection().subscribe(
                `/${this.getSignalNamespace(signal)}/${signal}`,
                (data: any) => callbacks[signal](data)
              );
            }
            subscribe();
          },
          errorCallback: () =>
            console.error(`Can not subscribe on "${signal}" signal`)
        })
      );

      currentSignal = signal;
    };

    subscribe();
  }

  private getSignalNamespace(signal: RemSignal): string {
    this.checkConfig("clientType");

    return [
      RemSignal.twilioJoinRequested,
      RemSignal.twilioLeaveRequested
    ].includes(signal)
      ? "twilio"
      : [RemSignal.positionChanged, RemSignal.availableTopicsChanged].includes(
          signal
        )
      ? "queue"
      : signal === RemSignal.recvFrom
      ? "hub"
      : [RemSignal.ticketsCountChanged, RemSignal.incidentChanged].includes(
          signal
        )
      ? "ticket"
      : this.config.clientType!;
  }

  public setSignalCallback(signal: RemSignal, callback: Function): void {
    this.signalCallbacks[signal] = callback;

    this.getWsConnection().subscribe(
      `/${this.getSignalNamespace(signal)}/${signal}`,
      callback
    );
  }

  public setNetworkCallbacks(
    callbacks: Record<"onLost" | "onReconnect", Function>
  ): void {
    this.networkCallbacks = callbacks;
  }

  public unsubscribeFromSignals(): void {
    this.checkConfig("clientType");

    const connection = this.getWsConnection();
    const targetEvents =
      this.config.clientType === "kiosk"
        ? [
            RemSignal.positionChanged,
            RemSignal.callRequested,
            RemSignal.hangupRequested,
            RemSignal.membershipRequested,
            RemSignal.message,
            RemSignal.endpointStatusChanged
          ]
        : [
            RemSignal.answerRequested,
            RemSignal.hangupRequested,
            RemSignal.resultReady,
            RemSignal.message,
            RemSignal.endpointStatusChanged,
            RemSignal.statusChanged,
            RemSignal.recvFrom
          ];

    targetEvents.forEach(ev =>
      connection.unsubscribe(`/${this.getSignalNamespace(ev)}/${ev}`)
    );
    this.signalCallbacks = {};
  }

  public sendSignalResponse(
    requestId: number,
    isSuccess: boolean,
    message: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/set_response`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [requestId, isSuccess, message],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getAvailableExperts(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/get_available_experts",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getExperts(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/get_experts",
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getKiosks(
    callback: (kiosks: KioskDTO[]) => void,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/get_kiosks",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public requestCall(
    deviceId: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/call_kiosk",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [deviceId],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public transferCall(
    expert: ExpertDTO,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/transfer",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [expert.extension],
            callback: (status: number) => {
              if (status === 0) {
                callback(status);
              } else if (errorCallback) {
                errorCallback("Can not transfer the call");
              }
            }
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public changeCallTopic(
    topic: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/change_topic",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [topic],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public answerCall(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/answer",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public hangup(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/expert/hangup",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getRoom(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/room/current",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getRooms(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/room/get_rooms",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public setActiveRoom(
    id: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/room/set_active",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [id, true],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public removeRoom(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/room/remove",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: ["", true],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public startSurvey(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/queue/start_survey",
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public holdCall(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/on_hold`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [true],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public resumeCall(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/on_hold`,
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [false],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getRtcConnectionConfig(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/get_rtc_connection_config`,
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public sendToHub(
    params: {
      receiverId: string;
      serviceName: string;
      message: {};
      messageId: string;
      isError?: boolean;
    },
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/hub/send_to",
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            body: [
              params.receiverId,
              params.serviceName,
              JSON.stringify(params.message),
              params.messageId,
              params.isError ?? false
            ],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public setStatistic(
    data: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/set_statistics`,
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            body: [data],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public setClientContext(
    data: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt", "clientType"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: `/${this.config.clientType}/set_client_context`,
            host: this.config.remServiceUrl!,
            jwt: this.config.jwt,
            body: [data],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getIncident(callback: Function, errorCallback?: Function) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/get_incident",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public startProcessingIncident(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/start_processing_incident",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [uuid],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
  public closeIncident(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/close_incident",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [uuid],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
  public createTicket(
    incidentUuid: string,
    payload: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/create_ticket",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [incidentUuid, payload],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }

  public getTicketsCount(callback: Function, errorCallback?: Function): void {
    this.checkConfig(["remServiceUrl", "jwt"]);

    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/get_tickets_count",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
  public getTicket(callback: Function, errorCallback?: Function): void {
    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/get_ticket",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
  public closeTicket(
    uuid: string,
    payload: object,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.connectIfNotWithCallback(() =>
      this.getWsConnection().sendRequest(
        _.assign(
          {
            method: "/ticket/close_ticket",
            host: this.config.remServiceUrl ?? "",
            jwt: this.config.jwt,
            body: [uuid, payload],
            callback
          },
          errorCallback ? { errorCallback } : {}
        )
      )
    );
  }
}

export class StagingRemService extends AbstractService
  implements RemServiceInterface {
  public getTelephonyType(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () => callback(TelephonyType.webex),
      this.config.stagingRequestDuration
    );
  }

  public getTopics(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          T1: {
            description: [[31, "T1"]],
            experts: ["{f4b430ea-3004-4c7d-8b51-df03b43cbc7f}"],
            kiosks: ["{5d16beb2-89b7-11ec-a8a3-0242ac120002}"],
            priority: 0,
            title: [[31, "T1"]]
          },

          T2: {
            description: [[31, "T2"]],
            experts: ["{f4b430ea-3004-4c7d-8b51-df03b43cbc7f}"],
            kiosks: ["{5d16beb2-89b7-11ec-a8a3-0242ac120002}"],
            priority: 0,
            title: [[31, "T2"]]
          }
        }),
      this.config.stagingRequestDuration
    );
  }

  public getKioskTopics(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          T1: {
            description: [[31, "T1"]],
            experts: ["{f4b430ea-3004-4c7d-8b51-df03b43cbc7f}"],
            kiosks: ["{5d16beb2-89b7-11ec-a8a3-0242ac120002}"],
            priority: 0,
            title: [[31, "T1"]]
          },

          T2: {
            description: [[31, "T2"]],
            experts: ["{f4b430ea-3004-4c7d-8b51-df03b43cbc7f}"],
            kiosks: ["{5d16beb2-89b7-11ec-a8a3-0242ac120002}"],
            priority: 0,
            title: [[31, "T2"]]
          }
        }),
      this.config.stagingRequestDuration
    );
  }

  public getAvailableTopics(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          T1: [0],
          T2: [0]
        }),
      this.config.stagingRequestDuration
    );
  }

  public getSessionInfo(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          // eslint-disable-next-line @typescript-eslint/camelcase
          client_id: "fakeId",
          extension: "fakeEmail",
          // eslint-disable-next-line @typescript-eslint/camelcase
          ip_addresses: ["10.0.0.0"],
          status: 2,
          topic: "FakeTopic"
        }),
      this.config.stagingRequestDuration
    );
  }

  public getStatus(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => {
      callback();
    }, this.config.stagingRequestDuration);
  }

  public setStatus(
    status: ClientStatus,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public getEndpointStatus(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          details: "Ready",
          extension: "2012",
          parties: [],
          status: EndpointStatus.ready
        }),
      this.config.stagingRequestDuration
    );
  }

  public setEndpointStatus(
    status: EndpointStatus | number,
    details: string,
    parties: string[],
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public acquire(
    target: string,
    foreignId: string,
    context: object,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(1), this.config.stagingRequestDuration);
  }

  public getHints(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => {
      callback(1111);
    }, this.config.stagingRequestDuration);
  }

  public removeFromQueue(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public getQueuePosition(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(-1), this.config.stagingRequestDuration);
  }

  public getQueueList(callback: Function, errorCallback?: Function): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          // eslint-disable-next-line @typescript-eslint/camelcase
          T1: [{ id: uuidv4(), device_id: uuidv4(), queue_time: 123 }]
        }),
      this.config.stagingRequestDuration
    );
  }

  public sendCallJoinRequest(
    extension: string,
    personId: string,
    webexRoomId: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public sendJsonMessage(
    message: {},
    correlationId: string | null,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public subscribeOnSignals(
    callbacks: Record<string, Function>,
    callback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback?.(), this.config.stagingRequestDuration);
  }

  public setSignalCallback(signal: RemSignal, callback: Function): void {
    /* empty */
  }

  public setNetworkCallbacks(
    callbacks: Record<"onLost" | "onReconnect", Function>
  ): void {
    /* empty */
  }

  public unsubscribeFromSignals(): void {
    /* empty */
  }

  public sendSignalResponse(
    requestId: number,
    isSuccess: boolean,
    message: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    /* empty */
  }

  public getAvailableExperts(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          "{5bfbd38c-cd85-4849-aadb-1ce23402f519}": {
            capabilities: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              interactive_desktop_sharing: true
            },
            details: "",
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_id: "{5bfbd38c-cd85-4849-aadb-1ce23402f519}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_name: "",
            extension: "test1@gmail.com",
            id: "{5bfbd38c-cd85-4849-aadb-1ce23402f519}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            ip_addresses: ["127.0.0.1"],
            languages: [],
            // eslint-disable-next-line @typescript-eslint/camelcase
            last_topic: "",
            load: 0,
            location: "",
            // eslint-disable-next-line @typescript-eslint/camelcase
            mustang_extension: "",
            name: "vadym-test-exp",
            status: 2,
            // eslint-disable-next-line @typescript-eslint/camelcase
            time_zone: 0,
            topics: []
          },
          "{f62e0260-6680-4e3b-8e75-818d0410fcd3}": {
            capabilities: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              interactive_desktop_sharing: true
            },
            details: "",
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_id: "{f62e0260-6680-4e3b-8e75-818d0410fcd3}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_name: "",
            extension: "test2@gmail.com",
            id: "{f62e0260-6680-4e3b-8e75-818d0410fcd3}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            ip_addresses: ["10.15.15.3", "192.168.50.54"],
            languages: [],
            // eslint-disable-next-line @typescript-eslint/camelcase
            last_topic: "Ashwath Dev",
            load: 11,
            location: "",
            // eslint-disable-next-line @typescript-eslint/camelcase
            mustang_extension: "",
            name: "ashwath-agent",
            status: 2,
            // eslint-disable-next-line @typescript-eslint/camelcase
            time_zone: 0,
            topics: []
          }
        }),
      this.config.stagingRequestDuration
    );
  }

  public getExperts(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback([
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            call_duration: 0,
            capabilities: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              interactive_desktop_sharing: true
            },
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_id: "{5bfbd38c-cd85-4849-aadb-1ce23402f519}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_name: "vadym-test-exp",
            // eslint-disable-next-line @typescript-eslint/camelcase
            endpoint_status: {
              details: "Ready",
              extension: "2005",
              parties: [],
              status: 2
            },
            // eslint-disable-next-line @typescript-eslint/camelcase
            ip_addresses: ["127.0.0.1"],
            languages: [],
            // eslint-disable-next-line @typescript-eslint/camelcase
            last_topic: "",
            load: 0,
            location: "",
            name: "vadym-test-exp",
            status: 1,
            // eslint-disable-next-line @typescript-eslint/camelcase
            time_zone: 0
          },
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            call_duration: 0,
            capabilities: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              interactive_desktop_sharing: true
            },
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_id: "{c25918c1-217a-44bc-808d-6fa9608daf84}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_name: "AntonioREWS",
            // eslint-disable-next-line @typescript-eslint/camelcase
            endpoint_status: {
              details: "Ready",
              extension: "2012",
              parties: [],
              status: 2
            },
            // eslint-disable-next-line @typescript-eslint/camelcase
            ip_addresses: ["127.0.0.1"],
            languages: [],
            // eslint-disable-next-line @typescript-eslint/camelcase
            last_topic: "",
            load: 0,
            location: "",
            name: "AntonioREWS",
            status: 2,
            // eslint-disable-next-line @typescript-eslint/camelcase
            time_zone: 0
          },
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            call_duration: 0,
            capabilities: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              interactive_desktop_sharing: true
            },
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_id: "{58da5789-9fad-445a-aca9-4f520fdcb665}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            display_name: "This IS DESCRIPTION!!",
            // eslint-disable-next-line @typescript-eslint/camelcase
            endpoint_status: {
              details: "Ready",
              extension: "2013",
              parties: [],
              status: 2
            },
            // eslint-disable-next-line @typescript-eslint/camelcase
            ip_addresses: ["127.0.0.1"],
            languages: [],
            // eslint-disable-next-line @typescript-eslint/camelcase
            last_topic: "Test",
            load: 12,
            location: "",
            name: "vasoREWS",
            status: 1,
            // eslint-disable-next-line @typescript-eslint/camelcase
            time_zone: 0
          }
        ]),
      this.config.stagingRequestDuration
    );
  }

  public getKiosks(
    callback: (kiosks: KioskDTO[]) => void,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => {
      callback([
        {
          capabilities: {
            // eslint-disable-next-line @typescript-eslint/camelcase
            interactive_desktop_sharing: true
          },
          // eslint-disable-next-line @typescript-eslint/camelcase
          device_id: "{bb64ab7b-aa11-4bf1-bc47-dc432b099a14}",
          // eslint-disable-next-line @typescript-eslint/camelcase
          display_name: "Kiosk in Da Venecia #1",
          // eslint-disable-next-line @typescript-eslint/camelcase
          endpoint_status: {
            details: "",
            extension: "{bb64ab7b-aa11-4bf1-bc47-dc432b099a14}",
            parties: [],
            status: 3
          },
          // eslint-disable-next-line @typescript-eslint/camelcase
          ip_addresses: ["10.0.0.61"],
          // eslint-disable-next-line @typescript-eslint/camelcase
          ipip_printer_address: "",
          // eslint-disable-next-line @typescript-eslint/camelcase
          ipip_printer_enabled: false,
          // eslint-disable-next-line @typescript-eslint/camelcase
          ipip_printer_model: "",
          languages: [],
          location: "Venecia",
          name: "Sergei",
          // eslint-disable-next-line @typescript-eslint/camelcase
          time_zone: 0
        },
        {
          capabilities: {
            // eslint-disable-next-line @typescript-eslint/camelcase
            interactive_desktop_sharing: true
          },
          // eslint-disable-next-line @typescript-eslint/camelcase
          device_id: "{eb53c452-64e4-4cb3-b279-cf9237cb9720}",
          // eslint-disable-next-line @typescript-eslint/camelcase
          display_name: "SlavaIEC",
          // eslint-disable-next-line @typescript-eslint/camelcase
          endpoint_status: {
            details: "",
            extension: "{eb53c452-64e4-4cb3-b279-cf9237cb9720}",
            parties: [],
            status: 2
          },
          // eslint-disable-next-line @typescript-eslint/camelcase
          ip_addresses: ["10.0.0.209"],
          // eslint-disable-next-line @typescript-eslint/camelcase
          ipip_printer_address: "0.0.0.0",
          // eslint-disable-next-line @typescript-eslint/camelcase
          ipip_printer_enabled: false,
          // eslint-disable-next-line @typescript-eslint/camelcase
          ipip_printer_model: "",
          languages: [],
          location: "",
          name: "SlavaIEC",
          // eslint-disable-next-line @typescript-eslint/camelcase
          time_zone: 331
        }
      ]);
    }, this.config.stagingRequestDuration);
  }

  public requestCall(
    deviceId: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(15), this.config.stagingRequestDuration);
  }

  public transferCall(
    expert: ExpertDTO,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(0), this.config.stagingRequestDuration);
  }

  public changeCallTopic(
    topic: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public answerCall(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public hangup(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public getRoom(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      (): RoomDTO =>
        callback({
          // eslint-disable-next-line @typescript-eslint/camelcase
          creation_time: 1671724980,
          description:
            "Appointment Anton Rudenko with Anton Rudenko at 2022-12-22 16:03:00",
          expert: {
            // eslint-disable-next-line @typescript-eslint/camelcase
            device_id: "{c4693ffd-95e6-4c4c-b7ec-a51a3d7293e9}",
            id: "{c4693ffd-95e6-4c4c-b7ec-a51a3d7293e9}",
            // eslint-disable-next-line @typescript-eslint/camelcase
            is_connected: true
          },
          id: "2b7471b9-4665-4a08-a800-f4903d484f33",
          // eslint-disable-next-line @typescript-eslint/camelcase
          idle_time: 0,
          // eslint-disable-next-line @typescript-eslint/camelcase
          joined_clients: [
            {
              // eslint-disable-next-line @typescript-eslint/camelcase
              device_id: "{3d1077cd-5220-4b6f-8606-684e8eb25622}",
              id: "{e9294e80-6ad2-4211-8951-ea0809849d33}",
              // eslint-disable-next-line @typescript-eslint/camelcase
              is_connected: true
            }
          ],
          lifetime: 900,
          // eslint-disable-next-line @typescript-eslint/camelcase
          taken_at: 1671725295,
          topic: ""
        }),
      this.config.stagingRequestDuration
    );
  }

  public getRooms(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      (): RoomDTO =>
        callback([
          {
            // eslint-disable-next-line @typescript-eslint/camelcase
            creation_time: 1671724980,
            description:
              "Appointment Anton Rudenko with Anton Rudenko at 2022-12-22 16:03:00",
            expert: {
              // eslint-disable-next-line @typescript-eslint/camelcase
              device_id: "{c4693ffd-95e6-4c4c-b7ec-a51a3d7293e9}",
              id: "{c4693ffd-95e6-4c4c-b7ec-a51a3d7293e9}",
              // eslint-disable-next-line @typescript-eslint/camelcase
              is_connected: true
            },
            id: "2b7471b9-4665-4a08-a800-f4903d484f33",
            // eslint-disable-next-line @typescript-eslint/camelcase
            idle_time: 0,
            // eslint-disable-next-line @typescript-eslint/camelcase
            joined_clients: [
              {
                // eslint-disable-next-line @typescript-eslint/camelcase
                device_id: "{3d1077cd-5220-4b6f-8606-684e8eb25622}",
                id: "{e9294e80-6ad2-4211-8951-ea0809849d33}",
                // eslint-disable-next-line @typescript-eslint/camelcase
                is_connected: true
              }
            ],
            lifetime: 900,
            // eslint-disable-next-line @typescript-eslint/camelcase
            taken_at: 1671725295,
            topic: "",
            // eslint-disable-next-line @typescript-eslint/camelcase
            is_active: true
          }
        ]),
      this.config.stagingRequestDuration
    );
  }

  public setActiveRoom(
    id: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public removeRoom(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public startSurvey(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public holdCall(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public resumeCall(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public getRtcConnectionConfig(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          iceServers: []
        }),
      this.config.stagingRequestDuration
    );
  }

  public sendToHub(
    params: {
      receiverId: string;
      serviceName: string;
      message: {};
      messageId: string;
      isError?: boolean;
    },
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public setStatistic(
    data: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public setClientContext(
    data: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(callback, this.config.stagingRequestDuration);
  }

  public getIncident(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          id: "{314daa45-4ef6-4172-89a1-1a95efb7c2a0}",
          // eslint-disable-next-line @typescript-eslint/camelcase
          created_at: 1703073280493,
          topic: "T2",
          type: IncidentType.none,
          // eslint-disable-next-line @typescript-eslint/camelcase
          in_process: false
        }),
      this.config.stagingRequestDuration
    );
  }

  public startProcessingIncident(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }
  public closeIncident(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }
  public createTicket(
    incidentUuid: string,
    payload: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public getTicketsCount(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () => callback({ total: 3, assigned: 0 }),
      this.config.stagingRequestDuration
    );
  }
  public getTicket(callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          id: "{8e02ab8f-afda-4bd4-8741-0a6f27cc78d7}",
          // eslint-disable-next-line @typescript-eslint/camelcase
          incident_payload: {
            name: "Jon Snow",
            phone: "+11111111111"
          }
        }),
      this.config.stagingRequestDuration
    );
  }
  public closeTicket(
    uuid: string,
    payload: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public disconnect() {
    /*empty*/
  }
}
